import { useAuth } from "@/context/AuthContext";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

interface AdminRouteGuardProps {
  children: ReactNode;
  requiredRole?: "admin" | "superadmin";
}

export function AdminRouteGuard({
  children,
  requiredRole = "admin",
}: AdminRouteGuardProps) {
  const { user, isAdmin, isSuperAdmin } = useAdminAccess();
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Check if the user has required role
  const hasAccess =
    isAdmin &&
    (requiredRole === "admin" ||
      (requiredRole === "superadmin" && isSuperAdmin));

  // Remove log
  // if (import.meta.env.DEV) {
  //   console.log("AdminRouteGuard access check:", {
  //     isAuthenticated,
  //     isLoading,
  //     hasAccess,
  //     requiredRole,
  //     userRole: user?.role,
  //     path: location.pathname,
  //     tokenExists: !!localStorage.getItem("token")
  //   });
  // }

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // If not authenticated, we'll redirect to login below
      toast.error("You need to log in to access the admin area");
      return;
    }

    if (!isLoading && isAuthenticated && !hasAccess) {
      // User is authenticated but doesn't have admin access
      toast.error("You don't have permission to access the admin area");

      console.error("Unauthorized access attempt to admin route", {
        path: location.pathname,
        userRole: user?.role,
        requiredRole,
        userId: user?.id,
        userName: user?.name,
      });
    }
  }, [
    isAuthenticated,
    isLoading,
    hasAccess,
    location.pathname,
    user,
    requiredRole,
  ]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-propertylk-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // If not authenticated (check token presence directly during transition), redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // If authenticated (token exists) but not admin, redirect to dashboard
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // User is authenticated and has admin access
  return <>{children}</>;
}
